/**
 * Switch Dev User Page
 *
 * Allows switching between dev users during local development.
 * Only accessible when DEV_MODE is active (VITE_DEV_MODE=true AND localhost).
 *
 * Security: This page is protected by multiple layers:
 * 1. Frontend: isDevMode check redirects away if not in dev mode
 * 2. Frontend: VITE_DEV_MODE requires localhost hostname
 * 3. Frontend: VITE_DEV_MODE is tree-shaken from production builds
 * 4. Backend: Dev tokens only accepted when Cognito is not configured (never in staging/prod)
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/auth/hooks/useAuth';

export function SwitchDevUserPage() {
  const { isDevMode, devUsers, user, switchDevUser } = useAuth();
  const navigate = useNavigate();

  // Redirect if not in dev mode - this should never render in staging/production
  if (!isDevMode) {
    return (
      <div>
        <div className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl font-bold text-foreground">Switch Dev User</h1>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">
                Dev user switching is only available in local development mode.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleSelectUser = (index: number) => {
    switchDevUser(index);
    navigate(-1);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-foreground">Switch Dev User</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Select a development user to switch to. This is only available in local development.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-6">
        {/* Security Notice */}
        <div className="rounded-md border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-4">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Local Development Only</span> — Dev users are protected
            by multiple security layers and cannot be used in staging or production environments.
          </p>
        </div>

        {/* User List */}
        <Card>
          <CardHeader>
            <CardTitle>Development Users</CardTitle>
            <CardDescription>
              All dev users have admin, contributor, and beta_tester roles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {devUsers.map((devUser, index) => {
                const isCurrentUser = devUser.userId === user?.userId;
                return (
                  <button
                    key={devUser.userId}
                    type="button"
                    onClick={() => handleSelectUser(index)}
                    disabled={isCurrentUser}
                    className={`flex items-center justify-between w-full px-4 py-3 rounded-md text-left transition-colors ${
                      isCurrentUser
                        ? 'bg-primary/10 border border-primary/30 cursor-default'
                        : 'hover:bg-accent border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isCurrentUser
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {devUser.email.charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <p
                          className={`text-sm font-medium ${isCurrentUser ? 'text-primary' : 'text-foreground'}`}
                        >
                          {devUser.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {devUser.userId} &middot; {devUser.username}
                        </p>
                      </div>
                    </div>
                    {isCurrentUser && (
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                        Current
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
